import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import FilterRow from './FilterRow';
import FilterPicker from './FilterPicker';
import FilterService from './FilterService';
import { getOperatorsForFieldType, operatorRequiresValue } from './operators';
import { Filter, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * FilterBuilder Component
 * Main component for building and managing filters based on SystemMetadataController
 */
function FilterBuilder({
  module,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  showApplyButton = true,
  showClearButton = true,
  initialFilters = [],
  onSaveView, // New prop: callback to save view
  columns = [], // New prop: column configuration
  sortConfig = [], // New prop: sort configuration
  datagridKey = null, // New prop: datagrid key identifier
}) {
  const [metadataFields, setMetadataFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(Array.isArray(initialFilters) ? initialFilters : []);
  const [showFilterPicker, setShowFilterPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [savingView, setSavingView] = useState(false);
  const hasInitializedRef = React.useRef(false);

  // Keep modal rows in sync with applied URL / parent filters (dashboard deep links).
  useEffect(() => {
    if (!Array.isArray(initialFilters)) return;

    const next = initialFilters.map((filter) => {
      const { logic, ...filterWithoutLogic } = filter;
      return filterWithoutLogic;
    });

    setFilters((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    );
    hasInitializedRef.current = true;
  }, [initialFilters]);

  // Computed values
  const canAddMore = useMemo(
    () => filters.length < metadataFields.length,
    [filters.length, metadataFields.length]
  );
  
  const allFieldsUsed = useMemo(
    () => filters.length >= metadataFields.length && metadataFields.length > 0,
    [filters.length, metadataFields.length]
  );

  // Load metadata on mount or when module changes
  useEffect(() => {
    if (!module) {
      setError('Module is required');
      setLoading(false);
      return;
    }

    const loadMetadata = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await FilterService.getFilterMetadata(module);
        
        if (result.success) {
          const sortedFields = [...result.fields].sort(
            (a, b) => (a.filter_order || 0) - (b.filter_order || 0)
          );
          setMetadataFields(sortedFields);
        } else {
          setError(result.error || 'Failed to load filter metadata');
        }
      } catch (err) {
        setError(err.message || 'Failed to load filter metadata');
      } finally {
        setLoading(false);
      }
    };

    loadMetadata();
  }, [module]);

  // Notify parent when filters change
  useEffect(() => {
    onFiltersChange?.(filters);
  }, [filters, onFiltersChange]);

  // Handlers with useCallback for performance
  const handleAddFilter = useCallback(() => {
    // Show filter picker
    setShowFilterPicker(true);
  }, []);

  // Handle field selection from filter picker
  const handleFieldSelect = useCallback((field) => {
    // Check if field is already in filters
    const isAlreadyAdded = filters.some(f => f.field === field.field_key);
    
    if (isAlreadyAdded) {
      // Remove the field if it's already selected (toggle off)
      setFilters(prev => prev.filter(f => f.field !== field.field_key));
      return;
    }

    // Add the field if it's not selected (toggle on)
    const operators = getOperatorsForFieldType(
      field.field_type,
      field.allowed_operators
    );
    const defaultOperator = field.default_operator || operators[0]?.value || 'equals';

    setFilters(prev => [...prev, {
      field: field.field_key,
      operator: defaultOperator,
      value: '',
    }]);

    // If not in multi-select mode, close the picker after adding
    if (!multiSelectMode) {
      setShowFilterPicker(false);
      setSearchQuery('');
    }
  }, [filters, multiSelectMode]);

  const handleFilterChange = useCallback((index, updatedFilter) => {
    setFilters(prev => {
      const newFilters = [...prev];
      // Remove logic field from updated filter
      const { logic, ...filterWithoutLogic } = updatedFilter;
      newFilters[index] = filterWithoutLogic;
      return newFilters;
    });
  }, []);

  const handleDeleteFilter = useCallback((index) => {
    setFilters(prev => {
      const newFilters = prev.filter((_, i) => i !== index);
      return newFilters;
    });
  }, []);

  const handleApplyFilters = useCallback(() => {
    if (!filters.length) {
      toast.error('Add a filter first (click "+ Add Filter"), then Apply.');
      return;
    }

    const hasUsableFilter = filters.some((filter) => {
      if (!filter?.field || !filter?.operator) return false;
      if (!operatorRequiresValue(filter.operator)) return true;
      if (filter.value === null || filter.value === undefined || filter.value === '') {
        return false;
      }
      if (Array.isArray(filter.value) && filter.value.length === 0) {
        return false;
      }
      return true;
    });

    if (!hasUsableFilter) {
      toast.error('Choose a field and value for your filter before applying.');
      return;
    }

    onApplyFilters?.(filters);
  }, [onApplyFilters, filters]);

  const handleClearFilters = useCallback(() => {
    setFilters([]);
    onClearFilters?.();
  }, [onClearFilters]);

  const handleSaveViewClick = useCallback(() => {
    setSaveViewModalOpen(true);
    setViewName('');
  }, []);

  const handleSaveViewCancel = useCallback(() => {
    setSaveViewModalOpen(false);
    setViewName('');
  }, []);

  const handleSaveViewConfirm = useCallback(async () => {
    if (!viewName.trim()) {
      return; // Don't save if name is empty
    }

    if (!onSaveView) {
      console.error('onSaveView callback is not provided');
      return;
    }

    setSavingView(true);
    try {
      // Prepare view data
      const viewData = {
        viewName: viewName.trim(),
        filters: filters, // Current filters
        sortConfig: sortConfig, // Sort configuration
        columns: columns, // Column configuration
        datagridKey: datagridKey || module, // Use datagridKey or fallback to module
      };

      await onSaveView(viewData);
      
      // Close modal and reset
      setSaveViewModalOpen(false);
      setViewName('');
    } catch (error) {
      console.error('Failed to save view:', error);
      // Error handling is done in parent component
    } finally {
      setSavingView(false);
    }
  }, [viewName, filters, sortConfig, columns, datagridKey, module, onSaveView]);

  // Get selected field keys for filter picker (must be before early returns)
  const selectedFieldKeys = useMemo(() => {
    return filters.map(f => f.field);
  }, [filters]);

  // Early returns for loading/error states
  if (loading) {
    return (
      <div className="ems-flt-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ems-flt-body">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  if (metadataFields.length === 0) {
    return (
      <div className="ems-flt-body">
        <Alert severity="info">No filterable fields available for this module.</Alert>
      </div>
    );
  }

  return (
    <>
    <div className="ems-flt-builder">
      {showFilterPicker ? (
        <div className="ems-flt-pick" style={{ flex: 1, minHeight: 300, display: 'flex', flexDirection: 'column' }}>
          <div className="ems-flt-pick-top">
            <div className="ems-flt-pick-title">Select Filters</div>
            <div className="ems-flt-pick-actions">
              <button
                type="button"
                className={`ems-btn ems-sm ${multiSelectMode ? 'ems-primary' : 'ems-ghost'}`}
                onClick={() => setMultiSelectMode(!multiSelectMode)}
              >
                {multiSelectMode ? <CheckBoxIcon fontSize="small" /> : <CheckBoxOutlineBlankIcon fontSize="small" />}
                Multi Select
              </button>
              <button
                type="button"
                className="ems-btn ems-sm ems-ghost"
                onClick={() => {
                  setShowFilterPicker(false);
                  setSearchQuery('');
                  setMultiSelectMode(false);
                }}
              >
                Cancel
              </button>
              {multiSelectMode && (
                <button
                  type="button"
                  className="ems-btn ems-sm ems-primary"
                  disabled={filters.length === 0}
                  onClick={() => {
                    setShowFilterPicker(false);
                    setSearchQuery('');
                    setMultiSelectMode(false);
                  }}
                >
                  Done
                </button>
              )}
            </div>
          </div>
          <div className="ems-flt-search">
            <SearchIcon fontSize="small" />
            <input
              type="text"
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery ? (
              <button
                type="button"
                className="ems-icon-btn"
                aria-label="Clear search"
                onClick={() => setSearchQuery('')}
              >
                ×
              </button>
            ) : null}
          </div>
          <div className="ems-flt-pick-scroll">
            <FilterPicker
              module={module}
              onFieldSelect={handleFieldSelect}
              selectedFields={selectedFieldKeys}
              searchQuery={searchQuery}
              multiSelectMode={multiSelectMode}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="ems-flt-body">
            {filters.length === 0 ? (
              <div className="ems-flt-empty">
                <div className="ems-flt-empty-ic" aria-hidden="true">
                  <Filter size={28} />
                </div>
                <div className="ems-flt-empty-title">No filters yet</div>
                <p className="ems-flt-empty-sub">
                  Narrow this list down by adding one or more field conditions. Combine them to build precise, reusable views.
                </p>
                <button
                  type="button"
                  className="ems-btn ems-primary"
                  onClick={handleAddFilter}
                  disabled={!canAddMore}
                >
                  <AddIcon fontSize="small" />
                  Add Filter
                </button>
              </div>
            ) : (
              <div className="ems-flt-rows">
                {filters.map((filter, index) => (
                  <div key={`${filter.field}-${index}`} className="ems-flt-row">
                    <FilterRow
                      filter={filter}
                      metadataFields={metadataFields}
                      onChange={(updatedFilter) => handleFilterChange(index, updatedFilter)}
                      onDelete={handleDeleteFilter}
                      index={index}
                      isLastRow={index === filters.length - 1}
                      onAddFilter={handleAddFilter}
                      canAddMore={canAddMore}
                    />
                  </div>
                ))}
                {allFieldsUsed && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', mt: 1 }}>
                    All available fields are being used. Delete a filter to add a new one.
                  </Typography>
                )}
              </div>
            )}
          </div>

          <div className="ems-flt-foot">
            {showClearButton && (
              <button
                type="button"
                className="ems-btn ems-ghost"
                onClick={handleClearFilters}
                title="Remove all filters"
              >
                <X size={16} />
                Clear all
              </button>
            )}
            <div className="ems-flt-spacer" />
            <button
              type="button"
              className="ems-btn ems-ghost"
              onClick={handleSaveViewClick}
              disabled={!onSaveView}
              title="Save view"
            >
              <Save size={16} />
              Save view
            </button>
            {showApplyButton && onApplyFilters && (
              <button
                type="button"
                className="ems-btn ems-primary"
                onClick={handleApplyFilters}
                title="Apply all filters"
              >
                <Filter size={16} />
                Apply
              </button>
            )}
          </div>
        </>
      )}
    </div>

    {/* Save View Modal */}
    <Dialog
      open={saveViewModalOpen}
      onClose={handleSaveViewCancel}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 12000 }}
    >
      <DialogTitle>Save View</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="View Name"
          type="text"
          fullWidth
          variant="outlined"
          value={viewName}
          onChange={(e) => setViewName(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && viewName.trim() && !savingView) {
              handleSaveViewConfirm();
            }
          }}
          disabled={savingView}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSaveViewCancel} disabled={savingView}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveViewConfirm}
          variant="contained"
          disabled={!viewName.trim() || savingView}
        >
          {savingView ? <CircularProgress size={20} /> : 'OK'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export default FilterBuilder;

